import React, { memo } from 'react';
import { Box } from '@saviynt/design-system';
import cx from 'classnames';

import { testIds } from '../../constants';

import './MarkDown.css';

const parseTextStyle = (text: string) =>
  text
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      `<a class='MarkDown-link' href='$2' target='_blank' rel='noopener noreferrer'>$1</a>`
    ) // Links: [text](url)
    .replace(/`([^`]+)`/g, `<code class='MarkDown-inlineCode'>$1</code>`) // Inline code (`code`)
    .replace(
      /\*\*\*(.*?)\*\*\*/g,
      `<span class='MarkDown-boldText'><i>$1</i></span>`
    ) // Bold and italic (*** bold and italic ***)
    .replace(/\*\*(.*?)\*\*/g, `<span class='MarkDown-boldText'>$1</span>`) // Bold (** bold **)
    .replace(/\*(.*?)\*/g, `<i>$1</i>`); // Italic (* italic *)

function ParseMarkDown({
  text,
  isAssistant,
  isAction,
}: {
  text: string;
  isAssistant: boolean;
  isAction: boolean;
}) {
  // Horizontal rule
  if (text.trim() === '---') {
    return <hr className='MarkDown-horizontalRule' />;
  }

  const className = cx(
    'MarkDown-typography-body2',
    isAssistant && 'MarkDown-typography-body2--isAssitant',
    isAction && 'MarkDown-typography-body2--isAction'
  );

  // Unordered list (- or *)
  if (/^(\*|-)\s+/.test(text)) {
    const content = text.replace(/^(\*|-)\s+/, '');
    const parsed = parseTextStyle(content);

    return (
      <li
        className={className}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: parsed }}
      />
    );
  }

  // Ordered list (1. 2. 3. ...)
  if (/^\d+\.\s+/.test(text)) {
    const content = text.replace(/^\d+\.\s+/, '');
    const parsed = parseTextStyle(content);

    return (
      <li
        className={className}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: parsed }}
      />
    );
  }

  // Normal text with bold/italic
  const parsed = parseTextStyle(text);

  return (
    <p
      className={className}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: parsed }}
    />
  );
}

interface MarkDownProps {
  isAssistant?: boolean;
  isAction?: boolean;
  children: string;
}

/**
 * MarkDown
 */
function MarkDown({ isAssistant, isAction, children }: MarkDownProps) {
  if (!children || typeof children !== 'string') {
    return null;
  }

  const lines = children.split('\n');

  // Group consecutive list items into <ul> or <ol>
  type ListState = {
    type: 'ul' | 'ol';
    items: React.ReactNode[];
  } | null;

  const getListClousure = (currentList: ListState, key: string) =>
    currentList?.type === 'ul' ? (
      <ul key={key} className='MarkDown-list-unordered'>
        {currentList.items}
      </ul>
    ) : (
      <ol key={key} className='MarkDown-list-ordered'>
        {currentList?.items}
      </ol>
    );

  let elementKey = 0;
  let currentList: ListState = null;
  const elements: React.ReactNode[] = [];

  lines.forEach((line, idx) => {
    let listType: 'ul' | 'ol' | null = null;
    const isUnorderedList = Boolean(/^(\*|-)\s+/.test(line));
    const isOrderedList = Boolean(/^\d+\.\s+/.test(line));

    if (isUnorderedList) {
      listType = 'ul';
    } else if (isOrderedList) {
      listType = 'ol';
    }

    if (listType) {
      // If starting a new list OR switching list type
      if (!currentList || currentList.type !== listType) {
        if (currentList) {
          elements.push(
            getListClousure(
              currentList,
              `list-${currentList.type}-${(elementKey += 1)}`
            )
          );
        }

        currentList = { type: listType, items: [] };
      }

      currentList.items.push(
        <ParseMarkDown
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          text={line}
          isAssistant={Boolean(isAssistant)}
          isAction={Boolean(isAction)}
        />
      );
    } else {
      // close list if open
      if (currentList) {
        elements.push(
          getListClousure(
            currentList,
            `list-${currentList.type}-${(elementKey += 1)}`
          )
        );
        currentList = null;
      }

      elements.push(
        <ParseMarkDown
          key={`element-${(elementKey += 1)}`}
          text={line}
          isAssistant={Boolean(isAssistant)}
          isAction={Boolean(isAction)}
        />
      );
    }
  });

  const typedCurrentList = currentList as {
    type: 'ul' | 'ol';
    items: React.ReactNode[];
  } | null;

  // flush last list if open
  if (typedCurrentList) {
    elements.push(
      getListClousure(
        typedCurrentList,
        `list-${typedCurrentList.type}-${(elementKey += 1)}`
      )
    );
  }

  return (
    <Box className='Markdown' dataTestId={testIds.GEN_MARKDOWN_ID}>
      {elements}
    </Box>
  );
}

MarkDown.defaultProps = {
  isAssistant: false,
  isAction: false,
};

export default memo(MarkDown);
